import { useCallback, useEffect, useRef, useState } from "react";

import {
  createThread as createThreadApi,
  deleteThread as deleteThreadApi,
  getThreads as getThreadsApi,
} from "../api/threads";
import { Thread } from "../types/thread";

export const useThreads = () => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use ref for new thread mode to avoid stale closures
  const isNewThreadModeRef = useRef(false);

  // Load threads on mount
  useEffect(() => {
    loadThreads();
     
  }, []);

  const createNewThread = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const newThread = await createThreadApi();
      setThreads((prev) => [newThread, ...prev]);
      setActiveThreadId(newThread.thread_id);
      isNewThreadModeRef.current = false;
      return newThread;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create thread";
      setError(errorMessage);
      console.error("Failed to create thread:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const selectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    isNewThreadModeRef.current = false;
  }, []);

  // Start a new thread locally (no API call - thread created on first message)
  const startNewThread = useCallback(() => {
    isNewThreadModeRef.current = true;
    setActiveThreadId(null);
  }, []);

  const deleteThread = useCallback(async (threadId: string) => {
    try {
      // Optimistically remove from UI
      setThreads((prev) =>
        prev.filter((thread) => thread.thread_id !== threadId),
      );
      setActiveThreadId((current) => (current === threadId ? null : current));

      // Delete from backend
      await deleteThreadApi(threadId);
    } catch (err) {
      console.error("Failed to delete thread:", err);
      // Reload threads to restore state if delete failed
      loadThreads();
    }
     
  }, []);

  const updateThread = useCallback(
    (threadId: string, updates: Partial<Thread>) => {
      setThreads((prev) =>
        prev.map((thread) =>
          thread.thread_id === threadId
            ? { ...thread, ...updates, updated_at: new Date().toISOString() }
            : thread,
        ),
      );

      // TODO: Sync with backend when update endpoint is available
    },
    [],
  );

  const loadThreads = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchedThreads = await getThreadsApi();
      setThreads(fetchedThreads);

      // Set the first thread as active if no active thread is selected
      // BUT don't auto-select if we're in "new thread" mode (check ref for latest value)
      if (!isNewThreadModeRef.current && fetchedThreads.length > 0) {
        setActiveThreadId((current) => {
          // Only set if still null (don't override if already selected)
          if (current === null) {
            return fetchedThreads[0].thread_id;
          }
          return current;
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load threads";
      setError(errorMessage);
      console.error("Failed to load threads:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    threads,
    activeThreadId,
    isLoading,
    error,
    createNewThread,
    startNewThread,
    selectThread,
    deleteThread,
    updateThread,
    loadThreads,
  };
};
